import { useState, useEffect, useRef, FormEvent } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { resendVerificationEmail } from '../../api/auth';
import { validateEmail, validatePassword } from '../../utils/validation';
import { Spinner } from '../Spinner';

const RESEND_COOLDOWN_SECONDS = 60;

interface HeroSectionProps {
  onLoginClick: () => void;
}

export function HeroSection({ onLoginClick }: HeroSectionProps) {
  const { register } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendMessage, setResendMessage] = useState('');
  const [isResending, setIsResending] = useState(false);
  const cooldownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Clear the cooldown interval on unmount so it can't call setState after the component is gone.
  useEffect(() => {
    return () => {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
      }
    };
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    if (!validatePassword(password)) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    const registerError = await register(email, password);
    if (registerError) {
      setError(registerError);
      setIsLoading(false);
      return;
    }

    // Registration triggers a verification email — show a "check your email" panel instead of
    // auto-logging in, since the account can't obtain an access token until it's verified.
    setIsLoading(false);
    setRegistered(true);
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || isResending) {
      return;
    }
    setResendMessage('');
    setIsResending(true);
    const { error: resendError } = await resendVerificationEmail(email);
    setIsResending(false);
    if (resendError) {
      setResendMessage(resendError);
      return;
    }
    setResendMessage('Verification email sent.');
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    cooldownIntervalRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownIntervalRef.current) {
            clearInterval(cooldownIntervalRef.current);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  return (
    <section
      id="hero"
      className="relative flex items-center min-h-screen px-6 md:px-12"
      style={{ paddingTop: 120, paddingBottom: 80, overflow: 'hidden' }}
    >
      {/* Background gradients */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 70% 40%, rgba(40,80,60,0.15), transparent), radial-gradient(ellipse 50% 40% at 20% 70%, rgba(100,60,20,0.08), transparent)',
        }}
      />
      <div
        className="absolute bottom-0 left-0 right-0 pointer-events-none"
        style={{
          height: 200,
          background: 'linear-gradient(to top, #110f0d, transparent)',
        }}
      />

      <div className="relative z-10 w-full max-w-[1200px] mx-auto grid grid-cols-1 xl:grid-cols-[1fr_420px] gap-12 xl:gap-20 items-center">
        {/* Left - pitch text */}
        <div className="xl:col-span-1 animate-[fadeUp_0.8s_ease_both]">
          <h1
            style={{
              fontFamily: "'Cinzel', serif",
              fontSize: 'clamp(36px, 5vw, 56px)',
              fontWeight: 700,
              lineHeight: 1.15,
              color: '#f0e8dc',
              letterSpacing: '0.02em',
              marginBottom: 8,
            }}
          >
            Command Your Province.
            <span
              className="block"
              style={{
                background: 'linear-gradient(135deg, #c8a84e, #a08030)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Shape Your Realm.
            </span>
          </h1>

          <p
            className="max-w-[520px] mb-8"
            style={{ fontSize: 20, color: '#e0d5c8', fontStyle: 'italic' }}
          >
            A browser-based kingdom management strategy game where intelligence is your greatest
            weapon and every decision echoes across the age.
          </p>

          <div className="flex flex-wrap gap-6 md:gap-8 mb-10">
            {[
              {
                label: 'Free to play',
                icon: (
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#e0d5c8" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                ),
              },
              {
                label: 'Real-time days',
                icon: (
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#e0d5c8" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12,6 12,12 16,14" />
                  </svg>
                ),
              },
              {
                label: 'Kingdom alliances',
                icon: (
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#e0d5c8" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                ),
              },
            ].map((f) => (
              <div key={f.label} className="flex items-center gap-2.5" style={{ color: '#e0d5c8', fontSize: 15 }}>
                <div
                  className="flex items-center justify-center shrink-0 rounded-full"
                  style={{
                    width: 32,
                    height: 32,
                    background: '#1c1916',
                    border: '1px solid #2e2924',
                  }}
                >
                  {f.icon}
                </div>
                {f.label}
              </div>
            ))}
          </div>

          <a
            href="#about"
            className="inline-flex items-center gap-2 px-8 py-3 rounded transition-colors hover:bg-game-800"
            style={{
              fontFamily: "'Cinzel', serif",
              fontSize: 14,
              fontWeight: 500,
              letterSpacing: '0.06em',
              color: '#e0d5c8',
              border: '1px solid #3a342d',
              textDecoration: 'none',
            }}
          >
            Learn more &darr;
          </a>
        </div>

        {/* Right - registration card */}
        <div
          className="relative max-w-[420px] w-full xl:col-span-1 animate-[fadeUp_0.8s_ease_0.2s_both]"
          style={{
            background: '#1c1916',
            border: '1px solid #2e2924',
            borderRadius: 8,
            padding: '36px 32px',
          }}
        >
          {/* Gold accent line */}
          <div
            className="absolute top-[-1px] left-6 right-6 h-[2px]"
            style={{ background: 'linear-gradient(90deg, transparent, #a08030, transparent)' }}
          />

          {registered ? (
            <div className="text-center">
              <h2
                className="mb-1"
                style={{ fontFamily: "'Cinzel', serif", fontSize: 18, fontWeight: 600, color: '#f0e8dc' }}
              >
                Check Your Email
              </h2>
              <p className="mb-6" style={{ color: '#b5a99c', fontSize: 14 }}>
                We sent a verification link to <strong>{email}</strong>. Click it to activate your account.
              </p>

              {resendMessage && (
                <div
                  className="mb-4 px-3 py-2 rounded text-sm"
                  style={{ background: '#14241a', border: '1px solid #205a3a', color: '#6a9e8e' }}
                >
                  {resendMessage}
                </div>
              )}

              <button
                type="button"
                onClick={handleResend}
                disabled={resendCooldown > 0 || isResending}
                className="w-full py-3 rounded font-semibold transition-opacity disabled:opacity-50"
                style={{
                  background: 'transparent',
                  color: '#6a9e8e',
                  fontFamily: "'Cinzel', serif",
                  fontSize: 13,
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  border: '1px solid #2e2924',
                  cursor: resendCooldown > 0 || isResending ? 'not-allowed' : 'pointer',
                }}
              >
                {isResending ? 'Sending...' : resendCooldown > 0 ? `Resend email (${resendCooldown}s)` : 'Resend email'}
              </button>
            </div>
          ) : (
            <>
              <h2
                className="text-center mb-1"
                style={{ fontFamily: "'Cinzel', serif", fontSize: 18, fontWeight: 600, color: '#f0e8dc' }}
              >
                Begin Your Reign
              </h2>
              <p className="text-center mb-6" style={{ color: '#635850', fontSize: 14 }}>
                Create your account in seconds
              </p>

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label
                    htmlFor="register-email"
                    className="block mb-1.5 uppercase tracking-wide"
                    style={{ fontFamily: "'Cinzel', serif", fontSize: 12, fontWeight: 500, color: '#b5a99c' }}
                  >
                    Email
                  </label>
                  <input
                    id="register-email"
                    type="email"
                    placeholder="player@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    className="w-full px-4 py-3 rounded focus:outline-none transition-colors"
                    style={{
                      background: '#0a0908',
                      border: '1px solid #2e2924',
                      color: '#f0e8dc',
                      fontSize: 16,
                    }}
                  />
                </div>

                <div className="mb-4">
                  <div className="flex justify-between items-center mb-1.5">
                    <label
                      htmlFor="register-password"
                      className="uppercase tracking-wide"
                      style={{ fontFamily: "'Cinzel', serif", fontSize: 12, fontWeight: 500, color: '#b5a99c' }}
                    >
                      Password
                    </label>
                    <span style={{ fontSize: 12, color: '#635850' }}>Min 8 characters</span>
                  </div>
                  <div className="relative">
                    <input
                      id="register-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Choose a password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isLoading}
                      className="w-full px-4 py-3 rounded focus:outline-none transition-colors"
                      style={{
                        background: '#0a0908',
                        border: '1px solid #2e2924',
                        color: '#f0e8dc',
                        fontSize: 16,
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 transition-colors hover:text-game-300"
                      style={{ background: 'none', border: 'none', color: '#635850', cursor: 'pointer' }}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="mb-4 px-3 py-2 rounded text-sm" style={{ background: '#2a1414', border: '1px solid #4a2020', color: '#ff5555' }}>
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3.5 rounded font-semibold transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{
                    background: 'linear-gradient(135deg, #2a4a4a, #1e3a3a)',
                    color: '#fff',
                    fontFamily: "'Cinzel', serif",
                    fontSize: 15,
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    border: '1px solid rgba(106,158,142, 0.3)',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {isLoading ? <><Spinner size="sm" /> Creating account...</> : 'Start Playing \u2192'}
                </button>
              </form>

              <div className="text-center mt-5" style={{ color: '#635850', fontSize: 13 }}>
                Already have an account?{' '}
                <button
                  onClick={onLoginClick}
                  className="hover:underline"
                  style={{ color: '#6a9e8e', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}
                >
                  Log in
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-\\[fadeUp_0\\.8s_ease_both\\],
          .animate-\\[fadeUp_0\\.8s_ease_0\\.2s_both\\] {
            animation: none !important;
            opacity: 1 !important;
            transform: none !important;
          }
        }
      `}</style>
    </section>
  );
}
